import { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { SortingState, OnChangeFn } from "@tanstack/react-table";
import { useOutletContext } from "react-router-dom";
import { toDateFormat } from "@/lib/dateHelpers";
import { showErrorSnackbar } from "@/lib/snackbarUtils";
import usePagination from "@/hooks/usePagination";
import Table, { TableDetails } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import MetricGrid from "@/components/MetricGrid";
import Button from "@/components/Button";
import SectionHeading from "@/components/SectionHeading";
import { EventDetailsSection } from "./_components/EventDetailsSection";
import { PayoutDetailsSection } from "./_components/PayoutDetailsSection";
import SubmitForPaymentModal from "./_components/SubmitForPaymentModal";
import MissingAddressesModal from "./_components/MissingAddressesModal";
import FileUploadModal from "./_components/UploadFileModal";
import { EXPENSE_DOCUMENTS_QUERY } from "./queries.ts";
import { ExpenseDocuments } from "./types.ts";
import { EventOutletContext } from "@/pages/events/[eventSlug]/(tabbed)/_layout";
import expenseForm from "@/assets/downloads/expense-report.pdf?url";
import useTableRowMenu from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import DeleteDocumentModal from "./_components/DeleteDocumentModal";
import IconButton from "@/components/IconButton/IconButton.tsx";
import useCapabilities, { PERMISSION } from "@/hooks/useCapabilities";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers.ts";

export default function PayoutsPage() {
  const event = useOutletContext<EventOutletContext>();
  const { eventData, eventRefetch } = useMemo(() => {
    return event;
  }, [event]);
  const { hasPermission } = useCapabilities();

  const [showSubmitForPaymentModal, setShowSubmitForPaymentModal] = useState(false);
  const [showMissingAddressesModal, setShowMissingAddressesModal] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [orderBy, setOrderBy] = useState({ field: "CREATED_AT", direction: "DESC" });
  const [sortingState, setSortingState] = useState<SortingState>([{ id: "uploadDate", desc: true }]);

  const { paginationVariables, renderFooter, resetPagination } = usePagination();

  const { data, loading, error, refetch } = useQuery<ExpenseDocuments>(EXPENSE_DOCUMENTS_QUERY, {
    variables: {
      eventId: eventData?.id,
      orderBy,
      ...paginationVariables
    },
    skip: !eventData?.id
  });

  const expenseDocumentsData = useMemo(() => {
    return (
      data?.expenseDocuments.edges.map(
        ({ node }: { node: { id: string; name: string; createdAt: string; url: string } }) => ({
          id: node.id,
          documentName: node.name,
          uploadDate: toDateFormat(node.createdAt),
          url: node.url
        })
      ) || []
    );
  }, [data]);

  const handleSubmitForPayment = () => {
    const missingExaminerAddress = !eventData?.patEventAttributes?.examiner?.contactAddress;
    const missingHostAddress = !eventData?.patEventAttributes?.hostProfessional?.contactAddress;

    const missingData = missingExaminerAddress || missingHostAddress;

    if (missingData) {
      setShowMissingAddressesModal(true);
    } else {
      setShowSubmitForPaymentModal(true);
    }
  };

  const handleFormDownload = () => {
    try {
      Object.assign(document.createElement("a"), {
        href: expenseForm,
        download: "expense-report.pdf"
      }).click();
    } catch (error) {
      showErrorSnackbar("Sorry, the form could not be downloaded. Please try again later.");
    }
  };

  const { submittedForPaymentAt = "", examinerFeePayee, examiner } = eventData?.patEventAttributes || {};
  const submittedForPaymentDate = toDateFormat(submittedForPaymentAt);

  const getPaymentRecipient = () => {
    if (examinerFeePayee === "Section") {
      return eventData?.section?.name || "--";
    }
    if (examinerFeePayee === "PAT Examiner") {
      return examiner?.displayName || "--";
    }
    return "--";
  };

  const handleSort: OnChangeFn<SortingState> = (updaterOrValue) => {
    setSortingState((prevSortingState) => {
      const newSortingState = typeof updaterOrValue === "function" ? updaterOrValue(prevSortingState) : updaterOrValue;

      const { id, desc } = newSortingState[0] || {};

      if (id !== "uploadDate") {
        return prevSortingState;
      }

      setOrderBy({
        field: "CREATED_AT",
        direction: desc ? "DESC" : "ASC"
      });

      resetPagination();

      return newSortingState;
    });
  };

  const handleRowClick = (row: { original: { url: string } }) => {
    const url = row.original.url;
    if (url) {
      window.open(url, "_blank");
    } else {
      showErrorSnackbar("Unable to open document. Please try again later.");
    }
  };

  const { Menu, setMenu } = useTableRowMenu();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<{ id: string; name: string } | null>(null);

  const handleDeleteClick = (doc: { id: string; documentName: string }) => {
    setSelectedDocument({ id: doc.id, name: doc.documentName });
    setDeleteModalOpen(true);
  };

  const canModifyDocuments = !submittedForPaymentDate || hasPermission(PERMISSION.editPAT);

  return (
    <>
      {eventData && (
        <div data-testid="payouts-page">
          <EventDetailsSection eventData={eventData} />
          <PayoutDetailsSection eventData={eventData} />
        </div>
      )}
      <SectionHeading heading="Fee Summary and Status" />
      <MetricGrid
        layout="expanded"
        hasHeading={true}
        metrics={[
          {
            label: "Total Amount Collected",
            value: centsToDollarsFormatted(eventData?.totalFeesCollected ?? null) ?? "--",
            iconName: "paid"
          },
          {
            label: "PAT National Fees",
            value: centsToDollarsFormatted(eventData?.registrationFeesCollected ?? null) ?? "--",
            iconName: "paid"
          },
          {
            label: "Onsite Fees",
            value: centsToDollarsFormatted(eventData?.courseFeesCollected ?? null) ?? "--",
            iconName: "paid"
          },
          {
            label: "Submitted for Payment",
            value: !!submittedForPaymentDate ? submittedForPaymentDate : "No",
            variant: !!submittedForPaymentDate ? "success" : "error",
            iconName: "receipt_long"
          },
          {
            label: "Payment Submitted To",
            value: getPaymentRecipient(),
            iconName: examinerFeePayee === "Section" ? "domain" : "account_circle"
          }
        ]}
      />
      <Table
        renderDetails={() => (
          <TableDetails
            heading="Expense Documentation"
            body=""
            ButtonComps={[
              () => (
                <>
                  <Button
                    variant="text"
                    icon="download"
                    onClick={() => handleFormDownload()}
                    testId="download-form-button"
                  >
                    Download Form
                  </Button>
                  {canModifyDocuments && (
                    <Button
                      variant="outlined"
                      icon="upload"
                      testId="upload-document-button"
                      onClick={() => setModalOpen(true)}
                    >
                      Upload Document
                    </Button>
                  )}
                  <FileUploadModal isOpen={isModalOpen} onClose={() => setModalOpen(false)} onSuccess={refetch} />
                  {!submittedForPaymentDate && (
                    <Button testId="submit-for-payment-button" onClick={() => handleSubmitForPayment()}>
                      Submit for Payment
                    </Button>
                  )}
                </>
              )
            ]}
          />
        )}
        data={expenseDocumentsData}
        loading={loading}
        columns={[
          {
            header: "Document Name",
            size: 250,
            id: "documentName",
            accessorKey: "documentName",
            enableSorting: false
          },
          {
            header: "Upload Date",
            accessorKey: "uploadDate",
            enableSorting: true
          },
          {
            id: "actions",
            header: "",
            size: 60,
            cell: (cellProps: any) => {
              const doc = cellProps.row.original;
              const actions: Action[] = [];

              if (canModifyDocuments) {
                actions.push({
                  text: "Delete Document",
                  iconName: "delete",
                  destructive: true,
                  onClick: () => handleDeleteClick(doc)
                });
              }

              if (actions.length === 0) {
                return null;
              }

              return (
                <div
                  className="text-right flex flex-row justify-end"
                  data-testid="menu-icon"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <IconButton
                    id={`action-${doc.id}`}
                    onClick={() => setMenu({ rowId: doc.id, actions })}
                    data-testid={`actions-${doc.id}`}
                    title="actions"
                    name="more_vert"
                    iconClassName="font-extrabold"
                    skipMargin
                  />
                </div>
              );
            },
            enableSorting: false
          }
        ]}
        sortingState={sortingState}
        onSortingChange={handleSort}
        serverSideSorting={true}
        renderNoDataEmptyState={() => (
          <EmptyState
            title={error ? "Error Loading Documents" : "No Associated Documents"}
            caption={
              error ? "There was an issue fetching documents." : "There are no documents associated with this event"
            }
            iconName="description"
          />
        )}
        data-testid="expense-documentation-table"
        renderFooter={renderFooter(data?.expenseDocuments)}
        onClickRow={handleRowClick}
      />
      {eventData && showMissingAddressesModal && (
        <MissingAddressesModal
          event={eventData}
          open={showMissingAddressesModal}
          onClose={() => setShowMissingAddressesModal(false)}
        />
      )}
      {eventData && showSubmitForPaymentModal && (
        <SubmitForPaymentModal
          event={eventData}
          onClose={() => setShowSubmitForPaymentModal(false)}
          refetch={eventRefetch}
        />
      )}
      <Menu />
      {deleteModalOpen && selectedDocument && (
        <DeleteDocumentModal
          onClose={() => setDeleteModalOpen(false)}
          documentId={selectedDocument.id}
          documentName={selectedDocument.name}
          refetch={refetch}
        />
      )}
    </>
  );
}
